import { Theme, Stack, Box } from '@mui/material';
import TabPanel from './tab-panel';
import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import useUserControl from '@/hooks/use-user-control.hook';
import { TabPanel as TabPanelType } from './helpers';
import TabBar from './tab-bar';
import useFeature from '@/hooks/use-feature.hook';
import { useTabs } from '@/hooks/use-tabs.hook';

interface Props {
  panels: TabPanelType[];
  variant?: 'page' | 'drawer';
  onChange?: (tabPath: string) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  drawerTabBar: {
    position: 'sticky',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    top: 0,
    zIndex: 10,
    margin: '0px -20px',
    padding: '10px 20px 0px',
  },
}));

const RouterTabs: FC<Props> = ({ panels, variant = 'page', onChange }) => {
  const styles = useStyles();
  const { tabPath, updateTabPath } = useTabs({ variant, onChange });
  const { checkLockedAndEmit } = useUserControl();
  const { hasFeature } = useFeature();

  const isPage = variant === 'page';

  const validPanels = useMemo(() => {
    return panels.filter(({ feature }) => !feature || hasFeature(feature));
  }, [hasFeature, panels]);

  const { PanelEl, tabIndex, withToolbar } = useMemo(() => {
    const matchingPanel = validPanels.find(({ path }) => path === tabPath);
    let panel: TabPanelType;
    if (matchingPanel && !matchingPanel.disabled) {
      panel = matchingPanel;
    } else {
      panel = validPanels.find(({ disabled }) => !disabled) || validPanels[0];
    }

    const tabIndex = validPanels.findIndex(({ path }) => path === panel.path);
    const { component: PanelEl, withToolbar } = panel;

    return { PanelEl, tabIndex, withToolbar };
  }, [validPanels, tabPath]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, tabIndex: number) => {
      if (checkLockedAndEmit(variant)) {
        return;
      }
      const targetPanel = validPanels[tabIndex];
      if (targetPanel?.path) {
        updateTabPath(targetPanel.path);
      }
    },
    [checkLockedAndEmit, validPanels, updateTabPath, variant]
  );

  const TabEl = <TabBar tabIndex={tabIndex} panels={validPanels} onTabChange={handleChange} />;

  return (
    <Stack>
      {isPage ? <>{TabEl}</> : <Box className={styles.drawerTabBar}>{TabEl}</Box>}
      <TabPanel tabIndex={tabIndex} withToolbar={withToolbar}>
        {PanelEl}
      </TabPanel>
    </Stack>
  );
};

export default RouterTabs;
